/**
 * FilesView - the Data Room. A read-focused document library over the
 * existing documents collection. Shows proposals and contracts with
 * kind/status filters, a stats row, and document cards. No editing
 * here - that happens in ContractsView or on the entity pages.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  FileSignature,
  FileText,
  Search,
  type LucideIcon,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { C } from '../lib/tokens';
import {
  bootstrapDocumentsIfEmpty,
  subscribeDocuments,
} from '../lib/db/documents';
import type { BusinessDoc, DocumentKind, DocumentStatus } from '../lib/db/types';

type KindFilter = 'all' | DocumentKind;

const STATUS_ORDER: DocumentStatus[] = [
  'draft',
  'sent',
  'viewed',
  'accepted',
  'signed',
  'declined',
  'expired',
];

/** Format a number as USD currency. */
function usd(n?: number): string {
  if (n == null) return '-';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/** Format an ISO timestamp as a readable date string. */
function formatDate(isoString: string): string {
  const dt = new Date(isoString);
  return dt.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Icon + color pair for a document kind. */
function kindMeta(kind: DocumentKind): {
  icon: LucideIcon;
  tone: string;
  label: string;
} {
  if (kind === 'proposal') {
    return {
      icon: FileText,
      tone: 'blue',
      label: 'Proposal',
    };
  }
  return {
    icon: FileSignature,
    tone: 'magenta',
    label: 'Contract',
  };
}

/** Color + label for a document status. */
function statusMeta(status: DocumentStatus): {
  tone: string;
  label: string;
} {
  const tones: Record<DocumentStatus, string> = {
    draft: 'orange',
    sent: 'blue',
    viewed: 'green',
    accepted: 'green',
    signed: 'green',
    declined: 'orange',
    expired: 'orange',
  };
  return {
    tone: tones[status],
    label: status.charAt(0).toUpperCase() + status.slice(1),
  };
}

/** Small inline empty state block. */
function EmptyState({
  icon: Icon,
  title,
  hint,
}: {
  icon: LucideIcon;
  title: string;
  hint?: string;
}) {
  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        gap: 8,
        textAlign: 'center',
        padding: '64px 24px',
      }}
    >
      <span
        style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          display: 'grid',
          placeItems: 'center',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Icon size={28} color={C.textDim} />
      </span>
      <div className="t-card-title" style={{ color: C.textMuted, marginTop: 4 }}>
        {title}
      </div>
      {hint && (
        <p className="t-meta" style={{ margin: '4px 0 0', maxWidth: 320 }}>
          {hint}
        </p>
      )}
    </div>
  );
}

export function FilesView() {
  const [docs, setDocs] = useState<BusinessDoc[]>([]);
  const [kind, setKind] = useState<KindFilter>('all');
  const [status, setStatus] = useState<DocumentStatus | 'all'>('all');
  const [q, setQ] = useState('');

  // Subscribe to documents and bootstrap if empty.
  useEffect(() => {
    bootstrapDocumentsIfEmpty().catch(() => undefined);
    const unsub = subscribeDocuments((all) => {
      setDocs(
        [...all].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)),
      );
    });
    return unsub;
  }, []);

  // Filter by kind, status, and search query.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return docs.filter((d) => {
      if (kind !== 'all' && d.kind !== kind) return false;
      if (status !== 'all' && d.status !== status) return false;
      if (
        needle &&
        !d.title.toLowerCase().includes(needle) &&
        !d.entityName.toLowerCase().includes(needle)
      ) {
        return false;
      }
      return true;
    });
  }, [docs, kind, status, q]);

  // Compute stats over the full doc set (not filtered).
  const totalValue = useMemo(
    () => docs.reduce((sum, d) => sum + (d.value ?? 0), 0),
    [docs],
  );

  const statusCounts = useMemo(() => {
    const base = kind === 'all' ? docs : docs.filter((d) => d.kind === kind);
    const counts: Record<string, number> = {};
    for (const d of base) counts[d.status] = (counts[d.status] ?? 0) + 1;
    return counts;
  }, [docs, kind]);

  const kindCounts = useMemo(
    () => ({
      all: docs.length,
      proposal: docs.filter((d) => d.kind === 'proposal').length,
      contract: docs.filter((d) => d.kind === 'contract').length,
    }),
    [docs],
  );

  return (
    <div className="app-container" style={{ display: 'grid', gap: 16 }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <span className="t-eyebrow">Files</span>
          <h1 className="t-page-title" style={{ margin: '6px 0 0' }}>
            Data Room
          </h1>
          <p className="t-meta" style={{ margin: '6px 0 0' }}>
            Every proposal and contract, organized by kind and status.
          </p>
        </div>
        <div style={{ position: 'relative' }}>
          <Search
            size={14}
            style={{
              position: 'absolute',
              top: '50%',
              left: 12,
              transform: 'translateY(-50%)',
              color: C.textDim,
            }}
          />
          <input
            className="glass-input"
            placeholder="Search by title or entity..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ paddingLeft: 34, width: 260 }}
          />
        </div>
      </header>

      {/* Summary stats row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 12,
        }}
      >
        <StatCard label="Documents" value={String(docs.length)} />
        <StatCard label="Total Value" value={usd(totalValue)} />
        <StatCard
          label="Proposals"
          value={String(kindCounts.proposal)}
        />
        <StatCard
          label="Contracts"
          value={String(kindCounts.contract)}
        />
      </div>

      {/* Filter pills (kind + status) */}
      <GlassPanel style={{ padding: 12 }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
          }}
        >
          {/* Kind filter */}
          <div style={{ display: 'flex', gap: 6 }}>
            {(['all', 'proposal', 'contract'] as KindFilter[]).map((k) => {
              const isActive = kind === k;
              const label =
                k === 'all'
                  ? 'All'
                  : k === 'proposal'
                    ? 'Proposals'
                    : 'Contracts';
              const Icon =
                k === 'proposal'
                  ? FileText
                  : k === 'contract'
                    ? FileSignature
                    : null;
              return (
                <button
                  key={k}
                  className="glass-btn"
                  onClick={() => setKind(k)}
                  style={{
                    background: isActive
                      ? 'rgba(255,0,255,0.14)'
                      : 'transparent',
                    color: isActive ? '#fff' : C.textMuted,
                    boxShadow: isActive
                      ? 'inset 0 0 0 1px rgba(255,0,255,0.45)'
                      : undefined,
                  }}
                >
                  {Icon && <Icon size={13} />}
                  {label}
                  <span className="t-meta" style={{ marginLeft: 4 }}>
                    {kindCounts[k]}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Status filter */}
          <div
            style={{
              display: 'flex',
              gap: 6,
              borderLeft: '1px solid rgba(255,255,255,0.1)',
              paddingLeft: 12,
            }}
          >
            {(['all', ...STATUS_ORDER] as (DocumentStatus | 'all')[]).map(
              (s) => {
                const isActive = status === s;
                const label = s === 'all' ? 'Any' : statusMeta(s as DocumentStatus).label;
                // The per-status counts come from `statusCounts`, whose base is
                // narrowed to the active kind. The "Any" count must use the SAME
                // kind-filtered total (kindCounts[kind]) — using docs.length here
                // made "Any N" disagree with the sum of the visible status chips
                // whenever a kind filter was active (e.g. kind=Proposals showed
                // "Any 20" over a status row that only summed the 8 proposals).
                const count = s === 'all' ? kindCounts[kind] : statusCounts[s] ?? 0;
                return (
                  <button
                    key={s}
                    className="glass-btn"
                    onClick={() => setStatus(s === 'all' ? 'all' : (s as DocumentStatus))}
                    style={{
                      background: isActive
                        ? 'rgba(255,0,255,0.14)'
                        : 'transparent',
                      color: isActive ? '#fff' : C.textMuted,
                      boxShadow: isActive
                        ? 'inset 0 0 0 1px rgba(255,0,255,0.45)'
                        : undefined,
                    }}
                  >
                    {label}
                    <span className="t-meta" style={{ marginLeft: 4 }}>
                      {count}
                    </span>
                  </button>
                );
              },
            )}
          </div>
        </div>
      </GlassPanel>

      {/* Document grid/list */}
      <GlassPanel style={{ padding: '8px 0' }}>
        {filtered.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={
              docs.length === 0
                ? 'No documents yet'
                : 'No documents match this filter'
            }
            hint={
              docs.length === 0
                ? 'Create a proposal or contract from an account or prospect page.'
                : 'Try adjusting your filters or search.'
            }
          />
        ) : (
          <div style={{ display: 'grid' }}>
            {filtered.map((doc) => {
              const km = kindMeta(doc.kind);
              const st = statusMeta(doc.status);
              return (
                <div
                  key={doc.id}
                  className="glass-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      '1fr 140px 110px 110px 120px',
                    gap: 12,
                    padding: '12px 22px',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    alignItems: 'center',
                  }}
                >
                  {/* Title + entity */}
                  <div style={{ minWidth: 0, display: 'grid', gap: 4 }}>
                    <div
                      className="t-card-title"
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {doc.title}
                    </div>
                    <div className="t-meta">
                      {doc.entityName}
                    </div>
                  </div>

                  {/* Kind pill */}
                  <span
                    className={`glass-pill glass-pill--${km.tone}`}
                    style={{
                      justifyContent: 'center',
                      fontSize: 11,
                      padding: '4px 8px',
                    }}
                  >
                    {km.label}
                  </span>

                  {/* Status pill */}
                  <span
                    className={`glass-pill glass-pill--${st.tone}`}
                    style={{
                      justifyContent: 'center',
                      fontSize: 11,
                      padding: '4px 8px',
                    }}
                  >
                    {st.label}
                  </span>

                  {/* Value */}
                  <div className="t-meta" style={{ textAlign: 'right' }}>
                    {usd(doc.value)}
                  </div>

                  {/* Updated date */}
                  <div className="t-meta" style={{ textAlign: 'right' }}>
                    {formatDate(doc.updatedAt)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </GlassPanel>
    </div>
  );
}

/** Small stat card for the header row. */
function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <GlassPanel style={{ padding: 16 }}>
      <div className="t-eyebrow" style={{ marginBottom: 6 }}>
        {label}
      </div>
      <div className="t-page-title" style={{ fontSize: 20, margin: 0 }}>
        {value}
      </div>
    </GlassPanel>
  );
}
